<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.candidateList.modal.update.buttonText')"
      @click="() => {
        openUpdateModal = true;
      }"
    />
    <export
      url="/campaign_candidates/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <candidate-comment-modal
    v-model:open="openEditModal"
    :candidate="selectedCandidateEntry"
    @saved="refetch"
  />
  <candidate-reason-modal
    v-model:open="openReasonModal"
    :candidate="selectedCandidateEntry"
    @saved="refetch"
  />
  <add-driver-modal
    v-model:open="openDriverModal"
    :campaign-group="modelValue"
    @saved="refetch" />
  <candidate-update-modal
    v-model:open="openUpdateModal"
    :campaign-group="modelValue"
    @saved="refetch"/>
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(reg) => ({ name: 'driver-details', params: { driverId: reg?.driver.id } })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
    :disable-frontend-sorting="true"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, type Ref, unref} from 'vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import type {Filter} from '@/components/utils/filter/Filter.vue';
import type {
  Address,
  CampaignCandidate,
  CampaignGroup,
  HydraCollection
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import AddDriverModal from '@pages/campaigns/campaignGroup/AddDriverModal.vue';
import type {Action as ActionType} from '@utils/table/Table.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import {format} from 'date-fns';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';
import CandidateCommentModal from "@pages/campaigns/campaignGroup/CandidateCommentModal.vue";
import CandidateUpdateModal from "@pages/campaigns/campaignGroup/CandidateUpdateModal.vue";
import CandidateReasonModal from "@pages/campaigns/campaignGroup/CandidateReasonModal.vue";

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.candidatesFromGroup', {
    group: props.modelValue?.name,
  });
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);

const openDriverModal = ref(false);
const openEditModal = ref(false);
const openReasonModal = ref(false);
const openUpdateModal = ref(false);

const selectedCandidateEntry = ref<CampaignCandidate | undefined>(undefined);

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => ({...fixedFilter.value, ...filter.value}));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions: ActionType[] = [
  {
    title: t('pages.campaigns.candidateList.actions.editComment'),
    action: (item: CampaignCandidate) => {
      selectedCandidateEntry.value = item;
      openEditModal.value = true;
    },
  },
  {
    title: t('pages.campaigns.candidateList.actions.submitAnswer'),
    action: (item: CampaignCandidate) => {
      selectedCandidateEntry.value = item;
      openReasonModal.value = true;
    },
  }
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'updatedTime',
    title: t('candidate.updatedTime'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('candidate.updatedTime'),
      },
    },
  },
  {
    key: 'driver.user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    visible: true,
    formatter: (v: string) => t(`title.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'status',
    csvTranslations: {
      unknown: t('candidate.enum.campaignCandidateStatus.unknown'),
      qualified: t('candidate.enum.campaignCandidateStatus.qualified'),
      disqualified: t('candidate.enum.campaignCandidateStatus.disqualified'),
    },
    title: t('candidate.status'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => v ? t(`candidate.enum.campaignCandidateStatus.${v}`) : "",
    filter: {
      type: 'dropdown',
      options: {
        options: enumOptionsProvider('/enums/CampaignCandidateStatus', (item) => ({
          label: t(`candidate.enum.campaignCandidateStatus.${item}`),
          value: item,
        })),
        label: t('candidate.status'),
      },
      group: t('pages.campaigns.candidateList.filterGroups.general'),
    },
  },
  {
    key: 'createdAt',
    title: t('candidate.addedTime'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('candidate.addedTime'),
      },
    },
  },
  {
    key: 'declineReason',
    csvTranslations: {
      NoTime: t('candidate.enum.declineReason.NoTime'),
      Holidays: t('candidate.enum.declineReason.Holidays'),
      SickOrInjured: t('candidate.enum.declineReason.SickOrInjured'),
      StolenBike: t('candidate.enum.declineReason.StolenBike'),
      BikeRepair: t('candidate.enum.declineReason.BikeRepair'),
      NoInterest: t('candidate.enum.declineReason.NoInterest'),
      BoxProblem: t('candidate.enum.declineReason.BoxProblem'),
      BadTopic: t('candidate.enum.declineReason.BadTopic'),
      Other: t('candidate.enum.declineReason.Other'),
      Partial: t('candidate.enum.declineReason.Partial'),
    },
    title: t('candidate.declineReason'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => v ? t(`candidate.enum.declineReason.${v}`) : "",
    filter: {
      type: 'dropdown',
      options: {
        options: enumOptionsProvider('/enums/CandidateDeclineReason', (item) => ({
          label: t(`candidate.enum.declineReason.${item}`),
          value: item,
        })),
        label: t('candidate.declineReason'),
      },
      group: t('pages.campaigns.candidateList.filterGroups.general'),
    },
  },
  {
    key: 'answerTime',
    title: t('candidate.answerTime'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    visible: true,
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('candidate.answerTime'),
      },
    },
  },
  {
    key: 'comment',
    title: t('candidate.comment'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    visible: true,
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('candidate.comment'),
      },
    },
  },
  {
    key: 'driver.rankStatus',
    title: t('driver.statusEarned'),
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/rankStatus'),
        label: t('driver.statusEarned'),
      },
    },
  },
  {
    key: 'driver.warningLevel',
    title: t('driver.warningLevel.DEFAULT'),
    csvTranslations: {
      none: t('driver.warningLevel.none'),
      first: t('driver.warningLevel.first'),
      second: t('driver.warningLevel.second'),
      third: t('driver.warningLevel.third'),
    },
    group: t('pages.campaigns.candidateList.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (level: string) => t(`driver.warningLevel.${level}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/WarningLevel', (v) => ({
          value: v,
          label: t(`driver.warningLevel.${v}`),
        })),
        label: t('driver.warningLevel.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.specialDriverPhoto',
    title: t('driver.specialPhoto'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('driver.specialPhoto'),
      },
    }
  },
  {
    key: 'driver.specialDriverStanding',
    title: t('driver.specialStanding'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('driver.specialStanding'),
      },
    }
  },
  {
    key: 'driver.helperDriver',
    title: t('driver.helper'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('driver.helper'),
      },
    },
  },
  {
    key: 'driver.noticeComment',
    title: t('driver.noticeComment.DEFAULT'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    formatter: (v?: string) => v ? t(`driver.noticeComment.${v}`) : '',
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/DriverNoticeComment', (v) => ({
          value: v,
          label: t(`driver.noticeComment.${v}`),
        })),
        label: t('driver.noticeComment.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.type',
    csvTranslations: {
      null: t('box.status.noBox'),
      v1: t('box.types.v1'),
      v2: t('box.types.v2'),
    },
    title: t('box.types.DEFAULT'),
    group: t('pages.campaigns.candidateList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ?? t('box.status.noBox'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxType'), [
          { label: t('box.status.noBox'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.type'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.types.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: t('box.variant.DEFAULT'),
    group: t('pages.campaigns.candidateList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : t('box.status.noBox')),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxVariant', (item) => ({
          value: item,
          label: t(`box.variant.${item}`),
        })), [
          { label: t('box.status.noBox'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.variant'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.variant.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.user.address.street',
    title: t('address.street'),
    group: t('pages.campaigns.candidateList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.address'),
      options: {
        label: t('address.street'),
      },
    },
  },
  {
    key: 'driver.user.address.cityName',
    title: t('address.city'),
    group: t('pages.campaigns.candidateList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.address'),
      options: {
        label: t('address.city'),
      },
    },
  },
  {
    key: 'driver.user.address.zip',
    title: t('address.zip'),
    group: t('pages.campaigns.candidateList.fieldGroups.address'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.candidateList.filterGroups.address'),
      options: {
        label: t('address.zip'),
      },
    },
  },
  {
    key: 'driver.defaultSourceAddress',
    title: `${t('driver.mostFrequentPath')} ${t('from')}`,
    group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['driver.defaultSourceAddress.street', 'driver.defaultSourceAddress.cityName'],
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'driver.defaultSourceAddress.street',
              type: 'text',
              group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('from')}`,
              },
            },
            {
              key: 'driver.defaultSourceAddress.zip',
              type: 'text',
              group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'driver.defaultSourceAddress.cityName',
              type: 'text',
              group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.defaultDestinationAddress',
    title: `${t('driver.mostFrequentPath')} ${t('to')}`,
    group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['driver.defaultDestinationAddress.street', 'driver.defaultDestinationAddress.cityName'],
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'driver.defaultDestinationAddress.street',
              type: 'text',
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('to')}`,
              },
            },
            {
              key: 'driver.defaultDestinationAddress.zip',
              type: 'text',
              group: t('pages.campaigns.candidateList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'driver.defaultDestinationAddress.cityName',
              type: 'text',
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    sortable: true,
    visible: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('candidate.createdAt'),
    group: t('pages.campaigns.candidateList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.candidateList.filterGroups.general'),
      options: {
        label: t('campaignRegistration.createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<CampaignCandidate>>('/campaign_candidates', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
